<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('stockAdjustment.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('stockAdjustment.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{
              $t('add_new')
              }}
            </ts-button>
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width:200px"
              @change="fetchData()"
            />
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-model="search"
              v-tooltip="$t('stockAdjustment.search')"
              :placeholder="$t('search')"
              style="width: 250px;"
              class="inline-block"
            />
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('stockAdjustment.branch') }}</label>
                  <ts-branch-filter
                    @filter="branchFilter" 
                    class="ts-w-full"
                  ></ts-branch-filter>
                </div>
              </div>
              <div class="row ts-mt-3">
                <div class="col-md-12">
                  <label class="">{{
                    $t('stockAdjustment.location')
                  }}</label>
                  <a-select
                    style="width: 257px"
                    v-model="location_id"
                    :options="locations"
                    showSearch
                    mode="multiple"
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                    :maxTagCount="1"
                    :maxTagTextLength="17"
                    :placeholder="$t('all')"
                  ></a-select>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <Table
          :data="resources" 
          :columns="columns" 
          size="small"
          highlight-row
          stripe
          max-height="700"
          :loading="loading"
        >
          <template slot-scope="{ row }" slot="branchName">
            {{ row.branch.branch_name_en }}
          </template>
          <template slot-scope="{ row }" slot="action">
              <a
                href="#"
                v-tooltip="$t('pdf')"
                class="mr-2 ml-2 text-red"
                @click.prevent="print(row)"
              >
                <i class="fas fa-file-pdf"></i>
              </a>
							<a href="#" v-tooltip="$t('edit')" class="text-primary mr-2" @click.prevent="edit(row)">
								<i class="fas fa-edit"></i>
							</a>
							<a-popconfirm
								placement="leftTop"
								:title="$t('are_you_sure_to_delete')"
								:ok-text="$t('yes')"
								:cancel-text="$t('no')"
								@confirm="deleteRecord(row)"
							>
								<a href="#" :disabled="row._deleting">
									<i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
									<i class="fa fa-trash text-red" v-else></i>
								</a>
							</a-popconfirm>
          </template>
        </Table>
      </div>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
      <a-modal :title="$t('pdf')" width="50%" v-model="show_pdf" :footer="null">
        <ts-preview-pdf v-model="waiting" :src="src" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState, mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import Reason from './components/reason_delete'

export default {
	name: 'stockAdjustment',
  components: {
    Reason
  },
	data(){
		return{
      loading: false,
      waiting: false,
      errors: new Errors(),
      show_pdf: false,
      src: '',
      locations: [],
      model: {
        deleted_reason: null
      }
		}
  },
  created(){
    this.fetchData()
    this.changeBranch([
      this.$store.state.authUser.user.default_branch.branch_id
    ])
  },
  computed: {
    ...mapState('stock/adjustment', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.stock.adjustment.search
      },
      set(newValue) {
        this.$store.commit('stock/adjustment/SET_SEARCH', newValue)
        this.$store.commit('stock/adjustment/RESET_CURRENT_PAGE')
      }
    },
    dateRange: {
      get() {
        return this.$store.state.stock.adjustment.dateRange
      },
      set(newValue) {
        this.$store.commit('stock/adjustment/SET_DATE_RANGE', newValue)
      }
    },
    location_id: {
      get () {
        return this.$store.state.stock.adjustment.location_id
      },
      set (newValue) {
        this.$store.commit('stock/adjustment/SET_LOCATION_ID', newValue)
      }
    },
    columns() {
      return [
        { 
          title: this.$t('stockAdjustment.branchName'),
          slot: 'branchName' 
        },
        { 
          title: this.$t('stockAdjustment.locationName'),
          key: 'location_name_en' 
        },
        { 
          title: this.$t('stockAdjustment.adjustmentDate'),
          key: 'adjustment_date' 
        },
        { 
          title: this.$t('stockAdjustment.adjustmentNumber'),
          key: 'adjustment_number' 
        },
        { 
          title: this.$t('stockAdjustment.approvedBy'),
          key: 'approver' 
        },
        { 
          title: this.$t('stockAdjustment.acknowledgeBy'),
          key: 'acknowledge' 
        },
        { 
          title: this.$t('stockAdjustment.remarks'),
          width: 240,
          key: 'remarks' 
        },
        {
					title: this.$t('actions'),
					key: 'action',
					slot: 'action',
					fixed: 'right',
					align: 'center',
					width: 120
				}
      ]
    }
  },
	methods: {
    ...mapActions('report/resource', ['getLocation']),

		addNew(){
			this.$router.push({ name: 'adjustment-create'})
		},
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('stock/adjustment/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit(record) {
			this.$store.commit('stock/adjustment/SET_EDIT_DATA', record)
			this.$router.push({ name: 'adjustment-edit', params:{ id: record.stock_adjustment_id }})
    },
    deleteRecord(record) {
      this.model.deleted_reason = null
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        content: h =>
          h(Reason, {
            props: {
              value: this.model,
              validate: this.errors,
              placeholder: this.$t('stockAdjustment.leaveReason')
            }
          }),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        onOk: () => {
          return this.$store.dispatch('stock/adjustment/destroy', {
            id: record.stock_adjustment_id,
            data: this.model
          })
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              this.errors = new Errors(error.errors)
              return Promise.reject(error)
            })
        },
        onCancel() {}
      })
    },
    print(record) {
      this.show_pdf = true
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('stock/adjustment/printPdf', {
          stock_adjustment_id: record.stock_adjustment_id,
        })
        .then((response) => {
          this.src = response.url
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    branchFilter (value) {
      this.$store.commit('stock/adjustment/SET_BRANCH_ID', value)
      this.$store.commit('stock/adjustment/SET_LOCATION_ID', [])
      this.changeBranch(value)
      this.fetchData()
    },
    getLocations () {
      this.loading = true
      this.getLocation()
        .then(response => {
          this.locations = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    changeBranch (value) {
      this.loading = true
      this.getLocation({
        branch_id: JSON.parse('[' + value + ']')
      })
        .then(response => {
          this.locations = response.data.map(el => ({
            value: el.location_id,
            label: el.location_name_en + ' | ' + el.branch.branch_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    }
	},
	watch: {
    search: debounce(function(search) {
      this.fetchData()
    }, 500),
    location_id: debounce(function () {
      this.fetchData(this.search)
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('stock/adjustment/RESET_STATE')
    next()
  }
}
</script>